import { Clock, Star } from 'lucide-react';
import { ImageWithFallback } from './figma/ImageWithFallback';
import type { StoreOpenLabel } from '@/lib/storeOpenLabel';

interface RestaurantCardProps {
  id: string;
  name: string;
  cuisine: string;
  rating: number;
  deliveryTime: string;
  distance: string;
  imageUrl: string;
  isOpen: boolean;
  showRating?: boolean;
  showDeliveryTime?: boolean;
  /**
   * Q7 박제 옵션 C — BE 4 필드 매트릭스 라벨 (menu#347 / menu#353).
   *
   * 제공 시 카드 내 영업 상태 표시에 매트릭스 라벨/톤을 사용 (영업중 / 임시
   * 오픈 / 영업시간 외 / 임시 휴무 등). 미제공 시 `isOpen` boolean 기준
   * 기존 "Cerrado" 오버레이 폴백.
   *
   * `hasQ7Hours=false` 인 응답 (hours relation 미로드) 에서는 호출자가
   * 본 prop 을 전달하지 않아 silent regression (outside_hours 미반영) 을
   * 차단해야 함.
   */
  q7Label?: StoreOpenLabel | null;
  isFavorite?: boolean;
  onFavoriteToggle?: (id: string) => void;
  onClick?: (id: string) => void;
  /**
   * 픽업 줄서기 게이트 매장(admin#2172/menu#804) — 앱 픽업 주문에 줄서기 called
   * 확인이 필요함을 알리는 배지 문구. 제공 시(비어있지 않은 문자열) 노출.
   */
  pickupWaitlistBadge?: string;
}

export function RestaurantCard({
  id,
  name,
  cuisine,
  rating,
  deliveryTime,
  distance,
  imageUrl,
  isOpen,
  showRating = true,
  showDeliveryTime = true,
  q7Label,
  isFavorite: _isFavorite = false,
  onFavoriteToggle: _onFavoriteToggle,
  onClick,
  pickupWaitlistBadge,
}: RestaurantCardProps) {
  // Q7 매트릭스 — primary/warning 은 영업 가능 (interactive), muted 만 비활성.
  // overlay 는 muted 일 때만 (영업종료 / 임시 휴무 / 영업시간 외) 표시.
  // warning (관리자 임시 오픈) 은 inline 배지로 표기 + interactive 유지.
  const interactive = q7Label ? q7Label.tone !== 'muted' : isOpen;
  const showOverlay = q7Label ? q7Label.tone === 'muted' : !isOpen;
  const overlayText = q7Label && q7Label.tone === 'muted' ? q7Label.text : 'Cerrado';
  const showWarningBadge = q7Label?.tone === 'warning';
  return (
    <div
      className={`bg-background rounded-[8px] shadow-sm border border-border
      p-4 mb-3 max-w-full overflow-hidden
      transition-all duration-200 ease-in-out ${
        interactive
          ? 'cursor-pointer hover:shadow-md hover:shadow-primary/10 active:scale-[0.98] hover:border-primary/20'
          : 'opacity-60 cursor-not-allowed'
      }`}
      onClick={() => interactive && onClick?.(id)}
    >
      <div className="flex gap-[min(3vw,1rem)]">
        {/* Restaurant Image */}
        <div className="relative w-[calc(4rem+2vw)] h-[calc(4rem+2vw)] shrink-0">
          <ImageWithFallback
            src={imageUrl}
            alt={name}
            className="w-full h-full object-cover rounded-[5px]"
          />
          {showOverlay && (
            <div className="absolute inset-0 bg-black/50 rounded-2xl flex items-center justify-center p-1">
              {/* Q2 옵션 B 박제 (menu#366) — line-clamp-2 + text-center 으로
                  긴 라벨 (예: "Cerrado hoy — abre 15:00") wrap 처리. */}
              <span
                className="text-white text-[calc(0.7rem+0.2vw)] font-medium text-center leading-tight line-clamp-2"
                data-testid="q7-muted-overlay"
              >
                {overlayText}
              </span>
            </div>
          )}
        </div>

        {/* Restaurant Info */}
        <div className="flex-1 min-w-0 h-[calc(4rem+2vw)] flex flex-col justify-between">
          <div className="flex items-start justify-between">
            <div className="flex-1 min-w-0">
              <h3 className="text-[15px] min-[600px]:text-base font-medium text-foreground truncate">
                {name}
              </h3>
              {pickupWaitlistBadge && (
                <span
                  className="inline-block mt-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-primary/10 text-primary line-clamp-2 max-w-full"
                  data-testid="pickup-waitlist-badge"
                >
                  {pickupWaitlistBadge}
                </span>
              )}
              {showWarningBadge && q7Label && (
                <span
                  // Q2 옵션 B 박제 (menu#366) — line-clamp-2 적용으로 좁은 카드 영역에서
                  // 긴 라벨 (예: "Disponible ahora (apertura temporal)") 의 overflow
                  // 차단. inline-block 유지 (Q1 옵션 A) + 2줄 wrap.
                  className="inline-block mt-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300 line-clamp-2 max-w-full"
                  data-testid="q7-warning-badge"
                >
                  {q7Label.text}
                </span>
              )}
            </div>
            {showRating && (
              <div className="flex items-center gap-1">
                <Star className="h-3 w-3 fill-primary text-primary" />
                <span className="text-[calc(0.75rem+0.125vw)] text-primary font-medium">
                  {rating}
                </span>
              </div>
            )}
          </div>
          
          <p className="text-[11px] min-[600px]:text-[12px] text-muted-foreground line-clamp-2 overflow-hidden">
            {cuisine}
          </p>
          
          <div className="flex items-center justify-between">
            {showDeliveryTime && (
              <div className="flex items-center gap-1">
                <Clock className="h-3 w-3 text-muted-foreground" />
                <span className="text-[calc(0.75rem+0.125vw)] text-muted-foreground">
                  {deliveryTime}
                </span>
              </div>
            )}
            {distance && (
              <span className="text-[calc(0.75rem+0.125vw)] text-muted-foreground">
                {distance}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
